class ForexWebSocket {
    constructor() {
      // ==========================================
      // CONNECTION
      // ==========================================
  
      this.socket = null;
      this.connected = false;
  
      // ==========================================
      // LISTENERS
      // ==========================================
  
      this.listeners = [];
  
      // ==========================================
      // REQUEST QUEUE
      // ==========================================
  
      this.queue = [];
  
      // ==========================================
      // RECONNECT
      // ==========================================
  
      this.reconnectTimer = null;
      this.shouldReconnect = true;
  
      this.reconnectDelay = 2000;
  
      // ==========================================
      // KEEP ALIVE
      // ==========================================
  
      this.pingTimer = null;
  
      // ==========================================
      // REQUEST ID
      // ==========================================
  
      this.reqId = 1;
  
      // ==========================================
      // ACTIVE SUBSCRIPTIONS
      // ==========================================
  
      this.subscriptions = new Map();
  
      // ==========================================
      // ACTIVE CANDLE REQUEST
      // ==========================================
  
      this.currentCandleKey = null;
  
      // ==========================================
      // URL
      // ==========================================
  
      this.url =
        "wss://api.derivws.com/trading/v1/options/ws/public";
    }
  
    // ==========================================
    // NEXT REQUEST ID
    // ==========================================
  
    nextReqId() {
      this.reqId += 1;
  
      return this.reqId;
    }
  
    // ==========================================
    // CONNECT
    // ==========================================
  
    connect() {
      // ------------------------------------------
      // Already OPEN
      // ------------------------------------------
  
      if (
        this.socket &&
        this.socket.readyState ===
          WebSocket.OPEN
      ) {
        return;
      }
  
      // ------------------------------------------
      // Already CONNECTING
      // ------------------------------------------
  
      if (
        this.socket &&
        this.socket.readyState ===
          WebSocket.CONNECTING
      ) {
        return;
      }
  
      this.shouldReconnect = true;
  
      clearTimeout(
        this.reconnectTimer
      );
  
      console.log(
        "🔌 Connecting to Deriv..."
      );
  
      try {
        this.socket =
          new WebSocket(
            this.url
          );
      } catch (error) {
        console.error(
          "❌ Failed to create WebSocket:",
          error
        );
  
        this.scheduleReconnect();
  
        return;
      }
  
      // ========================================
      // OPEN
      // ========================================
  
      this.socket.onopen =
        () => {
          console.log(
            "✅ Connected to Deriv"
          );
  
          this.connected = true;
  
          // Reset reconnect delay
          this.reconnectDelay =
            2000;
  
          // Start ping
          this.startPing();
  
          // Send queued requests
          this.flushQueue();
  
          // Request active symbols
          this.activeSymbols();
        };
  
      // ========================================
      // MESSAGE
      // ========================================
  
      this.socket.onmessage =
        (event) => {
          let data;
  
          try {
            data =
              JSON.parse(
                event.data
              );
          } catch (error) {
            console.error(
              "❌ Invalid WebSocket JSON:",
              error
            );
  
            return;
          }
  
          // ====================================
          // ERROR FROM API
          // ====================================
  
          if (data.error) {
            console.error(
              "❌ DERIV ERROR:",
              data.error.code,
              data.error.message
            );
          }
  
          // ====================================
          // SAVE SUBSCRIPTION ID
          // ====================================
  
          if (
            data.subscription
              ?.id
          ) {
            const echo =
              data.echo_req ||
              {};
  
            // Candle subscription
            if (
              echo.ticks_history &&
              echo.style ===
                "candles"
            ) {
              const key =
                this.makeCandleKey(
                  echo.ticks_history,
                  Number(
                    echo.granularity
                  )
                );
  
              this.subscriptions.set(
                key,
                data.subscription.id
              );
            }
  
            // Tick subscription
            else if (
              echo.ticks_history
            ) {
              const key =
                `ticks:${echo.ticks_history}`;
  
              this.subscriptions.set(
                key,
                data.subscription.id
              );
            }
          }
  
          // ====================================
          // NOTIFY LISTENERS
          // ====================================
  
          const callbacks = [
            ...this.listeners,
          ];
  
          callbacks.forEach(
            (callback) => {
              try {
                callback(data);
              } catch (error) {
                console.error(
                  "❌ Listener error:",
                  error
                );
              }
            }
          );
        };
  
      // ========================================
      // ERROR
      // ========================================
  
      this.socket.onerror =
        (error) => {
          console.error(
            "❌ WebSocket Error:",
            error
          );
        };
  
      // ========================================
      // CLOSE
      // ========================================
  
      this.socket.onclose =
        () => {
          console.log(
            "❌ Connection Closed"
          );
  
          this.connected =
            false;
  
          this.stopPing();
  
          this.socket = null;
  
          /*
           * Subscription IDs za connection
           * iliyofungwa hazitumiki tena.
           */
          this.subscriptions.clear();
  
          this.currentCandleKey =
            null;
  
          if (
            this.shouldReconnect
          ) {
            this.scheduleReconnect();
          }
        };
    }
  
    // ==========================================
    // RECONNECT
    // ==========================================
  
    scheduleReconnect() {
      if (
        !this.shouldReconnect
      ) {
        return;
      }
  
      clearTimeout(
        this.reconnectTimer
      );
  
      const delay =
        this.reconnectDelay;
  
      console.log(
        `🔄 Reconnecting in ${delay}ms...`
      );
  
      this.reconnectTimer =
        setTimeout(
          () => {
            this.connect();
          },
          delay
        );
  
      /*
       * Increase delay kidogo ikiwa
       * connection inaendelea kufeli.
       *
       * Max 10 seconds.
       */
      this.reconnectDelay =
        Math.min(
          this.reconnectDelay *
            1.5,
          10000
        );
    }
  
    // ==========================================
    // SEND
    // ==========================================
  
    send(payload) {
      if (!payload) {
        return;
      }
  
      // ------------------------------------------
      // Connected
      // ------------------------------------------
  
      if (
        this.socket &&
        this.socket.readyState ===
          WebSocket.OPEN
      ) {
        try {
          this.socket.send(
            JSON.stringify(
              payload
            )
          );
        } catch (error) {
          console.error(
            "❌ Send error:",
            error
          );
  
          this.queue.push(
            payload
          );
        }
  
        return;
      }
  
      // ------------------------------------------
      // Not connected -> queue
      // ------------------------------------------
  
      this.queue.push(
        payload
      );
  
      /*
       * Kama connect haijaanza,
       * ianzishe automatically.
       */
      this.connect();
    }
  
    // ==========================================
    // FLUSH QUEUE
    // ==========================================
  
    flushQueue() {
      if (
        !this.socket ||
        this.socket.readyState !==
          WebSocket.OPEN
      ) {
        return;
      }
  
      while (
        this.queue.length >
        0
      ) {
        const payload =
          this.queue.shift();
  
        try {
          this.socket.send(
            JSON.stringify(
              payload
            )
          );
        } catch (error) {
          console.error(
            "❌ Queue send error:",
            error
          );
  
          /*
           * Rudisha request mbele.
           */
          this.queue.unshift(
            payload
          );
  
          break;
        }
      }
    }
  
    // ==========================================
    // PING / KEEP CONNECTION ALIVE
    // ==========================================
  
    startPing() {
      this.stopPing();
  
      this.pingTimer =
        setInterval(
          () => {
            if (
              this.socket &&
              this.socket
                .readyState ===
                WebSocket.OPEN
            ) {
              try {
                this.socket.send(
                  JSON.stringify({
                    ping: 1,
                  })
                );
              } catch (error) {
                console.error(
                  "❌ Ping error:",
                  error
                );
              }
            }
          },
  
          // Every 30 seconds
          30000
        );
    }
  
    stopPing() {
      if (
        this.pingTimer
      ) {
        clearInterval(
          this.pingTimer
        );
  
        this.pingTimer =
          null;
      }
    }
  
    // ==========================================
    // ACTIVE SYMBOLS
    // ==========================================
  
    activeSymbols() {
      this.send({
        active_symbols:
          "full",
  
        req_id:
          this.nextReqId(),
      });
    }
  
    // ==========================================
    // TICK HISTORY
    // ==========================================
  
    history(
      symbol,
      count = 30
    ) {
      if (!symbol) {
        return;
      }
  
      this.send({
        ticks_history:
          symbol,
  
        count,
  
        end:
          "latest",
  
        style:
          "ticks",
  
        subscribe: 1,
  
        req_id:
          this.nextReqId(),
      });
    }
  
    // ==========================================
    // CANDLE KEY
    // ==========================================
  
    makeCandleKey(
      symbol,
      granularity
    ) {
      return (
        `candles: `+
        `${symbol}: `+
        `${granularity}`
      );
    }
  
    // ==========================================
    // CANDLES
    // ==========================================
  
    candles(
      symbol,
      granularity = 300,
      count = 150
    ) {
      if (!symbol) {
        return;
      }
  
      const seconds =
        Number(
          granularity
        );
  
      if (
        !Number.isFinite(
          seconds
        ) ||
        seconds <= 0
      ) {
        console.error(
          "❌ Invalid granularity:",
          granularity
        );
  
        return;
      }
  
      const key =
        this.makeCandleKey(
          symbol,
          seconds
        );
  
      // ========================================
      // SAME SUBSCRIPTION ALREADY ACTIVE
      // ========================================
  
      if (
        this.currentCandleKey ===
        key
      ) {
        console.log(
          "ℹ️ Candle subscription already active:",
          key
        );
  
        return;
      }
  
      // ========================================
      // FORGET OLD CANDLE SUBSCRIPTION
      // ========================================
  
      /*
       * TradingView yako inahitaji timeframe
       * moja kwa wakati.
       *
       * Kwa hiyo kabla ya kuomba mpya,
       * tunaweza kuondoa subscription ya zamani.
       */
  
      if (
        this.currentCandleKey &&
        this.currentCandleKey !==
          key
      ) {
        const oldSubscriptionId =
          this.subscriptions.get(
            this.currentCandleKey
          );
  
        if (
          oldSubscriptionId
        ) {
          this.forget(
            oldSubscriptionId
          );
  
          this.subscriptions.delete(
            this.currentCandleKey
          );
        }
      }
  
      this.currentCandleKey =
        key;
  
      // ========================================
      // REQUEST
      // ========================================
  
      this.send({
        ticks_history:
          symbol,
  
        adjust_start_time:
          1,
  
        count,
  
        end:
          "latest",
  
        style:
          "candles",
  
        granularity:
          seconds,
  
        subscribe: 1,
  
        req_id:
          this.nextReqId(),
      });
    }
  
    // ==========================================
    // FORGET SUBSCRIPTION
    // ==========================================
  
    forget(
      subscriptionId
    ) {
      if (
        !subscriptionId
      ) {
        return;
      }
  
      this.send({
        forget:
          subscriptionId,
  
        req_id:
          this.nextReqId(),
      });
    }
  
    // ==========================================
    // FORGET ALL
    // ==========================================
  
    forgetAll() {
      this.send({
        forget_all:
          "ticks",
  
        req_id:
          this.nextReqId(),
      });
  
      this.subscriptions.clear();
  
      this.currentCandleKey =
        null;
    }
  
    // ==========================================
    // LISTENER
    // ==========================================
  
    onTick(callback) {
      if (
        typeof callback !==
        "function"
      ) {
        return () => {};
      }
  
      /*
       * Zuia callback ile ile
       * kuongezwa mara mbili.
       */
      if (
        !this.listeners.includes(
          callback
        )
      ) {
        this.listeners.push(
          callback
        );
      }
  
      // ========================================
      // UNSUBSCRIBE LOCAL LISTENER
      // ========================================
  
      return () => {
        this.listeners =
          this.listeners.filter(
            (cb) =>
              cb !== callback
          );
      };
    }
  
    // ==========================================
    // REMOVE ALL LOCAL LISTENERS
    // ==========================================
  
    removeAllListeners() {
      this.listeners = [];
    }
  
    // ==========================================
    // CONNECTION STATUS
    // ==========================================
  
    isConnected() {
      return (
        this.socket &&
        this.socket
          .readyState ===
          WebSocket.OPEN
      );
    }
  
    // ==========================================
    // CLOSE COMPLETELY
    // ==========================================
  
    close() {
      console.log(
        "🔌 Closing WebSocket..."
      );
  
      this.shouldReconnect =
        false;
  
      // ------------------------------------------
      // Timers
      // ------------------------------------------
  
      clearTimeout(
        this.reconnectTimer
      );
  
      this.reconnectTimer =
        null;
  
      this.stopPing();
  
      // ------------------------------------------
      // State
      // ------------------------------------------
  
      this.connected =
        false;
  
      this.queue = [];
  
      this.subscriptions.clear();
  
      this.currentCandleKey =
        null;
  
      // ------------------------------------------
      // Socket
      // ------------------------------------------
  
      if (
        this.socket
      ) {
        /*
         * Ondoa handlers kwanza ili close
         * isisababishe reconnect.
         */
  
        this.socket.onopen =
          null;
  
        this.socket.onmessage =
          null;
  
        this.socket.onerror =
          null;
  
        this.socket.onclose =
          null;
  
        try {
          this.socket.close();
        } catch (error) {
          console.error(
            "❌ Close error:",
            error
          );
        }
  
        this.socket =
          null;
      }
    }
  }
  
  // ============================================
  // SINGLETON
  // ============================================
  
  const websocket =
    new ForexWebSocket();
  
  export default websocket;